import { patchState, signalStoreFeature } from '@ngrx/signals';
import { Events, withEventHandlers } from '@ngrx/signals/events';
import { mapResponse } from '@ngrx/operators';
import {
  catchError,
  concatMap,
  delay,
  EMPTY,
  filter,
  from,
  of,
  switchMap,
  tap,
} from 'rxjs';

import { TasksService } from '../../../services';
import { TASK_DETAIL_EVENTS, TASK_EVENTS } from '../../signal-events';
import {
  NON_CANCELLED_TASK_STATUSES,
  TaskInstanceDetail,
} from '@data-access/tasks/types';
import { extractBlobAndFilename, saveAs } from '@shared/utils/download.util';

/** Distinguishes consecutive question-thread lookups with identical outcomes. */
let _questionThreadLookupSeq = 0;

/**
 * A new question/section/document to-do reopens a prior SME/QC sign-off on that
 * entity (backend `TaskService.reopen_review_tasks_for_section` /
 * `DocumentTaskService.reopen_review_tasks_for_document`), so the task list, the
 * to-do count and the SME/QC list all need refetching alongside the task detail —
 * otherwise the SME/QC checkbox only reflects the reopened state after a full
 * page refresh.
 * @param workspaceID - The ID of the workspace the task was created on.
 * @param taskInstanceId - The ID of the newly created task instance.
 */
const _taskCreatedEvents = (workspaceID: string, taskInstanceId: string) => [
  TASK_EVENTS.getRemainingToDoTaskCount({ workspaceID }),
  TASK_EVENTS.getTaskList({ submissionStage: '', workspaceID }),
  TASK_EVENTS.getSMEQCTaskList({
    workspaceId: workspaceID,
    taskType: ['sme_mark_required', 'qc_mark_required'],
    isCompleted: undefined,
  }),
  TASK_DETAIL_EVENTS.getTaskDetail({ taskInstanceId, workspaceID }),
];

/**
 * Helper function to search for a task and either retrieve its details or create a new one
 * (document/section flows only — the question flow is search-only, ERM-12196)
 * @private
 * @returns Observable of task detail or creation event
 */
const _searchOrCreateTask = <TPayload>(options: {
  createEvent: (params: { payload: TPayload; workspaceID: string }) => unknown;
  payload: TPayload;
  searchParams: Record<string, unknown>;
}) => {
  const { searchParams, payload, createEvent } = options;
  return (taskListService: TasksService, workspaceID: string) =>
    taskListService.getTaskList(workspaceID, searchParams).pipe(
      filter(Boolean),
      concatMap((response) => {
        // if no task exists, create one
        if (response.length === 0) {
          return of(createEvent({ payload, workspaceID }));
        }
        // if a task already exists, fetch its details
        // and if a thread exists, fetch its messages
        const retrieveTaskInfo = [
          TASK_DETAIL_EVENTS.getTaskDetail({
            taskInstanceId: response[0].id,
            workspaceID,
          }),
          // Either fetch messages if a thread exists or patch with an empty array
          ...(response[0].thread?.id
            ? [
                TASK_DETAIL_EVENTS.getTaskMessages({
                  threadId: response[0].thread.id,
                  workspaceID,
                }),
              ]
            : [TASK_DETAIL_EVENTS.patchMessages({ messages: [] })]),
        ];
        return from(retrieveTaskInfo);
      }),
    );
};

export const withTaskDetailEffects = (
  _events: Events,
  _taskListService: TasksService,
) =>
  signalStoreFeature(
    withEventHandlers(({ ...store }) => ({
      addEntityToTask$: _events.on(TASK_DETAIL_EVENTS.addEntityToTask).pipe(
        mapResponse({
          error: () => {},
          next: (response) => {
            return TASK_DETAIL_EVENTS.patchTaskDetail({
              error: null,
              lastRefreshedTaskId: '',
              messageCommunicationEstablished: true,
              taskDetail: response.payload
                .data as unknown as TaskInstanceDetail,
            });
          },
        }),
      ),

      clearTaskDetails$: _events.on(TASK_DETAIL_EVENTS.clearTaskDetail).pipe(
        mapResponse({
          error: () => {},
          next: () => {
            patchState(store, { surveyInstance: null });
            return TASK_DETAIL_EVENTS.patchTaskDetail({
              error: null,
              lastRefreshedTaskId: '',
              messageCommunicationEstablished: false,
              taskDetail: null,
            });
          },
        }),
      ),

      createDocumentTask$: _events
        .on(TASK_DETAIL_EVENTS.createDocumentTask)
        .pipe(
          tap(() => patchState(store, { messages: null, taskDetail: null })),
          switchMap((event) => {
            const { workspaceID, payload } = event.payload;
            return _taskListService
              .createDocumentTask(workspaceID, payload)
              .pipe(
                filter(Boolean),
                concatMap((response) =>
                  from(_taskCreatedEvents(workspaceID, response.id)),
                ),
                catchError((error: Error) => {
                  patchState(store, { error });
                  return EMPTY;
                }),
              );
          }),
        ),

      createQuestionTask$: _events
        .on(TASK_DETAIL_EVENTS.createQuestionTask)
        .pipe(
          tap(() => patchState(store, { messages: null, taskDetail: null })),
          switchMap((event) => {
            const { workspaceID, payload } = event.payload;
            return _taskListService
              .createQuestionTask(workspaceID, payload)
              .pipe(
                filter(Boolean),
                concatMap((response) =>
                  from(_taskCreatedEvents(workspaceID, response.id)),
                ),
                catchError((error: Error) => {
                  patchState(store, { error });
                  return EMPTY;
                }),
              );
          }),
        ),
      createSectionTask$: _events.on(TASK_DETAIL_EVENTS.createSectionTask).pipe(
        tap(() => patchState(store, { messages: null, taskDetail: null })),
        switchMap((event) => {
          const { workspaceID, payload } = event.payload;
          return _taskListService.createSectionTask(workspaceID, payload).pipe(
            filter(Boolean),
            concatMap((response) =>
              from(_taskCreatedEvents(workspaceID, response.id)),
            ),
            catchError((error: Error) => {
              patchState(store, { error });
              return EMPTY;
            }),
          );
        }),
      ),

      // Create a new message in the task thread
      createTaskMessage$: _events.on(TASK_DETAIL_EVENTS.createTaskMessage).pipe(
        tap(() => patchState(store, { messages: null, taskDetail: null })),
        switchMap((event) => {
          const { workspaceID, taskInstanceId, message } = event.payload;
          return _taskListService
            .createThreadMessage(workspaceID, taskInstanceId, message)
            .pipe(
              filter(Boolean),
              concatMap((response) => {
                return from([
                  // refresh the remaining to-do task count
                  TASK_EVENTS.getRemainingToDoTaskCount({ workspaceID }),
                  // refresh the task list
                  TASK_EVENTS.getTaskList({ submissionStage: '', workspaceID }),
                  // fetch updated messages for the thread
                  TASK_DETAIL_EVENTS.getTaskMessages({
                    threadId: response?.thread_id ?? '',
                    workspaceID,
                  }),
                  // fetch updated task details
                  TASK_DETAIL_EVENTS.getTaskDetail({
                    taskInstanceId,
                    workspaceID,
                  }),
                ]);
              }),
            );
        }),
      ),

      // Export all messages in a task thread as a file
      exportThreadMessages$: _events
        .on(TASK_DETAIL_EVENTS.exportThreadMessages)
        .pipe(
          switchMap((event) => {
            const { workspaceID, threadId } = event.payload;
            return _taskListService
              .exportThreadMessages(workspaceID, threadId)
              .pipe(
                filter(Boolean),
                mapResponse({
                  error: (error: Error) => patchState(store, { error }),
                  next: (response) => {
                    const extracted = extractBlobAndFilename(response);
                    const { blob, filename } = extracted;
                    // Saves the file using the extracted blob and filename
                    saveAs(blob, filename);
                  },
                }),
              );
          }),
        ),

      getTaskDetails$: _events.on(TASK_DETAIL_EVENTS.getTaskDetail).pipe(
        tap(() =>
          patchState(store, { messageCommunicationEstablished: false }),
        ),
        switchMap((event) => {
          const { workspaceID, taskInstanceId } = event.payload;
          return _taskListService.getTaskById(workspaceID, taskInstanceId).pipe(
            delay(200),
            filter(Boolean),
            mapResponse({
              error: (error: Error) =>
                patchState(store, {
                  error,
                  messageCommunicationEstablished: false,
                }),
              next: (response) =>
                TASK_DETAIL_EVENTS.patchTaskDetail({
                  error: null,
                  lastRefreshedTaskId: '',
                  messageCommunicationEstablished: true,
                  taskDetail: response,
                }),
            }),
          );
        }),
      ),
      // Get all messages in a task thread
      getTaskMessages$: _events.on(TASK_DETAIL_EVENTS.getTaskMessages).pipe(
        switchMap((event) => {
          const { workspaceID, threadId } = event.payload;
          return _taskListService.getThreadMessages(workspaceID, threadId).pipe(
            filter(Boolean),
            mapResponse({
              error: (error: Error) => patchState(store, { error }),
              next: (response) =>
                TASK_DETAIL_EVENTS.patchMessages({ messages: response }),
            }),
          );
        }),
      ),

      patchMessages$: _events.on(TASK_DETAIL_EVENTS.patchMessages).pipe(
        mapResponse({
          error: () => {},
          next: (response) =>
            patchState(store, { messages: [...response.payload.messages] }),
        }),
      ),

      // Refresh task detail when a new message is created via websocket
      refreshTaskDetail$: _events.on(TASK_DETAIL_EVENTS.refreshTaskDetail).pipe(
        tap((event) => {
          // Update the refreshed task ID in the store so components can react
          patchState(store, {
            lastRefreshedTaskId: event.payload.taskDetailId,
          });
        }),
      ),

      // Event on search for a question task. If it exists, fetch its details and messages.
      searchDocumentTask$: _events
        .on(TASK_DETAIL_EVENTS.searchDocumentTask)
        .pipe(
          switchMap((event) => {
            const { workspaceID, payload } = event.payload;
            return _searchOrCreateTask({
              createEvent: TASK_DETAIL_EVENTS.createDocumentTask,
              payload,
              searchParams: {
                document_id: payload.document_id,
                status: [...NON_CANCELLED_TASK_STATUSES],
                task_types: ['document'],
              },
            })(_taskListService, workspaceID);
          }),
        ),
      // ERM-12196 — SEARCH-ONLY both flag states: a miss only records the
      // lookup; the BE create POST is COLLAB_HUB_REDESIGN-gated (404 off).
      searchQuestionTask$: _events
        .on(TASK_DETAIL_EVENTS.searchQuestionTask)
        .pipe(
          switchMap((event) => {
            const { workspaceID, payload } = event.payload;
            return _taskListService
              .getTaskList(workspaceID, {
                question_id: payload.question_id,
                status: [...NON_CANCELLED_TASK_STATUSES],
                task_types: ['question'],
              })
              .pipe(
                filter(Boolean),
                // A second click supersedes the first: the outer switchMap
                // already drops the stale request, and this keeps the events
                // it fans out on the same footing (Yogendra, PR 6496).
                switchMap((response) => {
                  const task = response[0] ?? null;
                  patchState(store, {
                    questionThreadLookup: {
                      questionId: payload.question_id,
                      questionTitle: payload.title,
                      seq: ++_questionThreadLookupSeq,
                      task,
                    },
                  });
                  if (!task) {
                    return EMPTY;
                  }
                  return from([
                    TASK_DETAIL_EVENTS.getTaskDetail({
                      taskInstanceId: task.id,
                      workspaceID,
                    }),
                    // Either fetch messages if a thread exists or patch empty
                    ...(task.thread?.id
                      ? [
                          TASK_DETAIL_EVENTS.getTaskMessages({
                            threadId: task.thread.id,
                            workspaceID,
                          }),
                        ]
                      : [TASK_DETAIL_EVENTS.patchMessages({ messages: [] })]),
                  ]);
                }),
              );
          }),
        ),

      searchSectionTask$: _events.on(TASK_DETAIL_EVENTS.searchSectionTask).pipe(
        switchMap((event) => {
          const { workspaceID, payload } = event.payload;
          return _searchOrCreateTask({
            createEvent: TASK_DETAIL_EVENTS.createSectionTask,
            payload,
            searchParams: {
              section_id: payload.section_id,
              status: [...NON_CANCELLED_TASK_STATUSES],
              task_types: ['section'],
            },
          })(_taskListService, workspaceID);
        }),
      ),
      updateTaskStatus$: _events.on(TASK_DETAIL_EVENTS.updateTaskStatus).pipe(
        switchMap((event) => {
          const {
            status: _status,
            taskInstanceId: _taskInstanceId,
            workspaceID: _workspaceID,
          } = event.payload;
          // TODO: Finish Implementation in followup PR
          return of(true);
        }),
      ),
    })),
  );
